import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, TriangleAlert } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Chargement,
  MarqueObligatoire,
  MessageErreur,
  Pastille,
  PastilleSite,
} from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { useAuth } from '../../auth/useAuth';
import { ErreurApi } from '../../lib/api';
import { horodatageReunion } from '../../lib/emargement-affichage';
import { dateReunion } from '../../lib/useFormateurs';
import {
  LIMITES_RECLAMATION,
  useCloturerReclamation,
  useNoterReclamation,
  useQualifierReclamation,
  useReclamation,
  useRouvrirReclamation,
  type GesteReclamation,
  type OptionsRegistre,
  type VueReclamation,
} from '../../lib/useReclamations';
import { delaiLisible, tonStatut } from './affichage';

const CLASSE_LIBELLE = 'block text-[11px] font-bold uppercase tracking-wide text-codialis-navy';
const CLASSE_CHAMP = 'mt-1 w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy';

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/**
 * Fiche d'une réclamation, côté équipe (TF-482) : qui réclame et comment il a
 * été informé de la traçabilité, ce qu'il dit, le traitement et son
 * historique. Les gestes possibles et ce qui les bloque viennent de l'API ; son
 * ouverture est tracée.
 */
export function FicheReclamation({
  id,
  options,
  onFermer,
}: {
  id: string;
  options: OptionsRegistre | undefined;
  onFermer: () => void;
}): JSX.Element {
  const { data, isLoading, error } = useReclamation(id);
  return (
    <Modale
      titre={data ? `${data.reference} · ${data.objet}` : 'Réclamation'}
      sousTitre={data ? `${data.statutLibelle} · reçue le ${dateReunion(data.recueLe)} · ${data.canalLibelle}` : undefined}
      ouverte
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-5 p-4 sm:p-6">
        {isLoading && <Chargement libelle="Chargement de la réclamation…" />}
        {error && <MessageErreur message={messageErreur(error, 'La réclamation n’a pas pu être ouverte.')} />}
        {data && <ContenuFiche reclamation={data} options={options} />}
      </div>
    </Modale>
  );
}

function Rubrique({ titre, children }: { titre: string; children: ReactNode }): JSX.Element {
  return (
    <section className="space-y-2">
      <h3 className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">{titre}</h3>
      {children}
    </section>
  );
}

function Champ({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-codialis-gray">{libelle}</dt>
      <dd className="break-words text-sm text-codialis-navy">{children}</dd>
    </div>
  );
}

function ContenuFiche({
  reclamation,
  options,
}: {
  reclamation: VueReclamation;
  options: OptionsRegistre | undefined;
}): JSX.Element {
  const { peutAcceder } = useAuth();
  const [geste, setGeste] = useState<GesteReclamation | null>(null);
  const cloture = reclamation.cloture;

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        <PastilleSite site={reclamation.siteCode} />
        <Pastille texte={reclamation.statutLibelle} ton={tonStatut(reclamation.statut)} />
        {reclamation.gravite && <Pastille texte={`Gravité ${reclamation.gravite.libelle.toLowerCase()}`} ton="neutral" />}
        <Pastille texte={delaiLisible(reclamation)} ton={reclamation.delai.horsDelai ? 'danger' : 'neutral'} />
      </div>

      <Rubrique titre="Réclamant">
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle={reclamation.qualiteLibelle}>
            {reclamation.nomReclamant}
            {reclamation.entreprise ? ` · ${reclamation.entreprise}` : ''}
          </Champ>
          <Champ libelle="Contact">{reclamation.contactReclamant ?? '—'}</Champ>
          {reclamation.formation && <Champ libelle="Formation">{reclamation.formation}</Champ>}
          <Champ libelle="Enregistrée">
            le {horodatageReunion(reclamation.deposee.le)}
            {reclamation.deposee.par ? ` par ${reclamation.deposee.par}` : ''}
          </Champ>
        </dl>
        <p className="flex items-start gap-2 rounded-xl bg-codialis-sand px-3 py-2 text-xs text-codialis-navy">
          <ShieldCheck className="mt-px h-4 w-4 shrink-0 text-codialis-teal" aria-hidden />
          <span className="min-w-0">
            {reclamation.information.confirmeePar
              ? `${reclamation.information.confirmeePar} a confirmé, le ${horodatageReunion(reclamation.information.le)}, avoir informé la personne que sa réclamation est enregistrée à son nom et tracée.`
              : `La personne a déposé elle-même, le ${horodatageReunion(reclamation.information.le)}, après avoir lu que sa réclamation est enregistrée à son nom et tracée.`}
          </span>
        </p>
        {reclamation.apprenantId && peutAcceder('learners') && (
          <Link
            to={`/learners/${reclamation.apprenantId}`}
            className="inline-flex min-h-[44px] items-center text-sm font-semibold text-codialis-sky hover:underline"
          >
            Ouvrir le dossier
          </Link>
        )}
      </Rubrique>

      <Rubrique titre="Réclamation">
        <p className="whitespace-pre-line break-words rounded-xl border border-gray-100 px-3 py-2 text-sm text-codialis-navy">
          {reclamation.description}
        </p>
      </Rubrique>

      <Rubrique titre="Traitement">
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Champ libelle="Catégorie">{reclamation.categorie ?? 'À qualifier'}</Champ>
          <Champ libelle="Gravité">{reclamation.gravite?.libelle ?? 'À qualifier'}</Champ>
          <Champ libelle="Responsable">{reclamation.responsable?.nom ?? 'À assigner'}</Champ>
          {reclamation.priseEnChargeLe && (
            <Champ libelle="Prise en charge">le {dateReunion(reclamation.priseEnChargeLe)}</Champ>
          )}
        </dl>
        {cloture && (
          <div className="space-y-2 rounded-xl border border-codialis-mint/40 bg-codialis-mint/10 px-3 py-3">
            <p className="text-xs text-codialis-navy">
              Clôturée le {horodatageReunion(cloture.le)}
              {cloture.par ? ` par ${cloture.par}` : ''} · conclusion : <strong>{cloture.conclusionLibelle}</strong>
            </p>
            <p className="whitespace-pre-line break-words text-sm text-codialis-navy">{cloture.reponse}</p>
            {cloture.actionCorrective && (
              <p className="break-words text-xs text-codialis-navy">
                <strong>Action corrective :</strong> {cloture.actionCorrective}
              </p>
            )}
            {cloture.amelioration && (
              <p className="break-words text-xs text-codialis-navy">
                <strong>Piste d’amélioration :</strong> {cloture.amelioration}
              </p>
            )}
          </div>
        )}
      </Rubrique>

      <Rubrique titre="Gestes">
        <div className="flex flex-wrap gap-2">
          {reclamation.gestes
            .filter((possible) => possible.refus === null)
            .map((possible) => (
              <button
                key={possible.geste}
                type="button"
                aria-pressed={geste === possible.geste}
                onClick={() => setGeste(geste === possible.geste ? null : possible.geste)}
                className={`inline-flex min-h-[44px] items-center rounded-xl px-4 text-sm font-semibold transition ${
                  geste === possible.geste
                    ? 'bg-codialis-navy text-white'
                    : 'border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                }`}
              >
                {possible.libelle}
              </button>
            ))}
        </div>
        {reclamation.gestes
          .filter((possible) => possible.refus !== null)
          .map((possible) => (
            <p
              key={possible.geste}
              className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            >
              <TriangleAlert className="mt-px h-4 w-4 shrink-0" aria-hidden />
              <span className="min-w-0">
                {possible.libelle} : {possible.refus}
              </span>
            </p>
          ))}
        {geste === 'QUALIFIER' && (
          <FormulaireQualification reclamation={reclamation} options={options} onFait={() => setGeste(null)} />
        )}
        {geste === 'NOTER' && <FormulaireNote reclamation={reclamation} onFait={() => setGeste(null)} />}
        {geste === 'CLOTURER' && (
          <FormulaireCloture reclamation={reclamation} options={options} onFait={() => setGeste(null)} />
        )}
        {geste === 'ROUVRIR' && <FormulaireReouverture reclamation={reclamation} onFait={() => setGeste(null)} />}
      </Rubrique>

      <Rubrique titre="Historique">
        <ol className="space-y-3 border-l-2 border-gray-100 pl-4">
          {reclamation.historique.map((evenement, rang) => (
            <li key={`${evenement.le}-${rang}`} className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-sm font-semibold text-codialis-navy">{evenement.libelle}</span>
                {evenement.visibleReclamant && <Pastille texte="Visible du réclamant" ton="info" />}
              </div>
              <p className="text-xs text-codialis-gray">
                {horodatageReunion(evenement.le)}
                {evenement.auteur ? ` · ${evenement.auteur}` : ''}
              </p>
              {evenement.changements.length > 0 && (
                <ul className="space-y-0.5 text-xs text-codialis-navy">
                  {evenement.changements.map((changement) => (
                    <li key={changement.champ} className="break-words">
                      {changement.champ} : {changement.avant ?? '—'} → {changement.apres ?? '—'}
                    </li>
                  ))}
                </ul>
              )}
              {evenement.texte && (
                <p className="whitespace-pre-line break-words rounded-lg bg-gray-50 px-2 py-1.5 text-xs text-codialis-navy">
                  {evenement.texte}
                </p>
              )}
            </li>
          ))}
        </ol>
      </Rubrique>
    </>
  );
}

function Actions({ enCours, libelle, onAnnuler, valide }: { enCours: boolean; libelle: string; onAnnuler: () => void; valide: boolean }): JSX.Element {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <BoutonSecondaire onClick={onAnnuler}>Annuler</BoutonSecondaire>
      <BoutonPrincipal type="submit" disabled={!valide || enCours}>
        {enCours ? 'Enregistrement…' : libelle}
      </BoutonPrincipal>
    </div>
  );
}

function FormulaireQualification({
  reclamation,
  options,
  onFait,
}: {
  reclamation: VueReclamation;
  options: OptionsRegistre | undefined;
  onFait: () => void;
}): JSX.Element {
  const id = useId();
  const toast = useToast();
  const qualifier = useQualifierReclamation();
  const [saisie, setSaisie] = useState({
    categorie: reclamation.qualification.categorie ?? '',
    gravite: reclamation.qualification.gravite ?? '',
    responsableId: reclamation.qualification.responsableId ?? '',
  });
  const valide = saisie.categorie !== '' && saisie.gravite !== '' && saisie.responsableId !== '';

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (!valide) return;
    qualifier.mutate(
      { id: reclamation.id, corps: saisie },
      {
        onSuccess: () => {
          toast(reclamation.statut === 'NOUVELLE' ? 'Réclamation prise en charge' : 'Qualification enregistrée');
          onFait();
        },
      },
    );
  };

  const choix = (cle: keyof typeof saisie, libelle: string, valeurs: { valeur: string; libelle: string }[]): JSX.Element => (
    <div className="min-w-0">
      <label htmlFor={`${id}-${cle}`} className={CLASSE_LIBELLE}>
        {libelle}
        <MarqueObligatoire />
      </label>
      <select
        id={`${id}-${cle}`}
        required
        value={saisie[cle]}
        onChange={(evenement) => setSaisie((avant) => ({ ...avant, [cle]: evenement.target.value }))}
        className={`${CLASSE_CHAMP} min-h-[44px]`}
      >
        <option value="">Choisir…</option>
        {valeurs.map((option) => (
          <option key={option.valeur} value={option.valeur}>
            {option.libelle}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <form onSubmit={envoyer} className="space-y-3 rounded-xl bg-gray-50 p-3 sm:p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {choix('categorie', 'Catégorie', options?.categories ?? [])}
        {choix('gravite', 'Gravité', options?.gravites ?? [])}
        {choix(
          'responsableId',
          'Responsable',
          reclamation.responsables.map((candidat) => ({ valeur: candidat.id, libelle: `${candidat.nom} · ${candidat.fonction}` })),
        )}
      </div>
      <p className="text-xs text-codialis-gray">
        Le responsable est prévenu par e-mail ; lui ou la direction clôture la réclamation.
      </p>
      {qualifier.error && <MessageErreur message={messageErreur(qualifier.error, 'La qualification n’a pas pu être enregistrée.')} />}
      <Actions enCours={qualifier.isPending} libelle="Enregistrer" onAnnuler={onFait} valide={valide} />
    </form>
  );
}

function ZoneTexte({
  id,
  libelle,
  valeur,
  onChange,
  max,
  obligatoire = false,
  lignes = 3,
  aide,
}: {
  id: string;
  libelle: string;
  valeur: string;
  onChange: (valeur: string) => void;
  max: number;
  obligatoire?: boolean;
  lignes?: number;
  aide?: string;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={CLASSE_LIBELLE}>
        {libelle}
        {obligatoire && <MarqueObligatoire />}
      </label>
      {aide && (
        <p id={`${id}-aide`} className="mt-0.5 text-xs text-codialis-gray">
          {aide}
        </p>
      )}
      <textarea
        id={id}
        required={obligatoire}
        rows={lignes}
        maxLength={max}
        value={valeur}
        aria-describedby={aide ? `${id}-aide` : undefined}
        onChange={(evenement) => onChange(evenement.target.value)}
        className={CLASSE_CHAMP}
      />
    </div>
  );
}

function FormulaireNote({ reclamation, onFait }: { reclamation: VueReclamation; onFait: () => void }): JSX.Element {
  const id = useId();
  const toast = useToast();
  const noter = useNoterReclamation();
  const [texte, setTexte] = useState('');
  const valide = texte.trim().length >= LIMITES_RECLAMATION.note.min;

  return (
    <form
      className="space-y-3 rounded-xl bg-gray-50 p-3 sm:p-4"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (!valide) return;
        noter.mutate(
          { id: reclamation.id, corps: { texte: texte.trim() } },
          {
            onSuccess: () => {
              toast('Note ajoutée à l’historique');
              onFait();
            },
          },
        );
      }}
    >
      <ZoneTexte
        id={`${id}-note`}
        libelle="Note de suivi"
        aide="Interne à l’équipe : la personne qui réclame ne la lit pas."
        valeur={texte}
        onChange={setTexte}
        max={LIMITES_RECLAMATION.note.max}
        obligatoire
      />
      {noter.error && <MessageErreur message={messageErreur(noter.error, 'La note n’a pas pu être ajoutée.')} />}
      <Actions enCours={noter.isPending} libelle="Ajouter la note" onAnnuler={onFait} valide={valide} />
    </form>
  );
}

function FormulaireCloture({
  reclamation,
  options,
  onFait,
}: {
  reclamation: VueReclamation;
  options: OptionsRegistre | undefined;
  onFait: () => void;
}): JSX.Element {
  const id = useId();
  const toast = useToast();
  const cloturer = useCloturerReclamation();
  const [saisie, setSaisie] = useState({ reponse: '', conclusion: '', actionCorrective: '', amelioration: '' });
  const valide = saisie.reponse.trim().length >= LIMITES_RECLAMATION.reponse.min && saisie.conclusion !== '';
  const changer = (cle: keyof typeof saisie) => (valeur: string) => setSaisie((avant) => ({ ...avant, [cle]: valeur }));

  return (
    <form
      className="space-y-3 rounded-xl bg-gray-50 p-3 sm:p-4"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (!valide) return;
        cloturer.mutate(
          {
            id: reclamation.id,
            corps: {
              reponse: saisie.reponse.trim(),
              conclusion: saisie.conclusion,
              ...(saisie.actionCorrective.trim() ? { actionCorrective: saisie.actionCorrective.trim() } : {}),
              ...(saisie.amelioration.trim() ? { amelioration: saisie.amelioration.trim() } : {}),
            },
          },
          {
            onSuccess: () => {
              toast('Réclamation clôturée');
              onFait();
            },
          },
        );
      }}
    >
      <ZoneTexte
        id={`${id}-reponse`}
        libelle="Réponse apportée"
        aide={
          reclamation.qualite === 'APPRENANT' || reclamation.qualite === 'FORMATEUR'
            ? 'La personne la lit dans « Mes réclamations » et en est prévenue par e-mail.'
            : 'Elle figure au registre ; transmettez-la à la personne par le canal de sa réclamation.'
        }
        valeur={saisie.reponse}
        onChange={changer('reponse')}
        max={LIMITES_RECLAMATION.reponse.max}
        obligatoire
        lignes={4}
      />
      <div className="min-w-0">
        <label htmlFor={`${id}-conclusion`} className={CLASSE_LIBELLE}>
          Conclusion
          <MarqueObligatoire />
        </label>
        <select
          id={`${id}-conclusion`}
          required
          value={saisie.conclusion}
          onChange={(evenement) => changer('conclusion')(evenement.target.value)}
          className={`${CLASSE_CHAMP} min-h-[44px] sm:w-72`}
        >
          <option value="">Choisir…</option>
          {(options?.conclusions ?? []).map((option) => (
            <option key={option.valeur} value={option.valeur}>
              {option.libelle}
            </option>
          ))}
        </select>
      </div>
      <ZoneTexte
        id={`${id}-action`}
        libelle="Action corrective"
        valeur={saisie.actionCorrective}
        onChange={changer('actionCorrective')}
        max={LIMITES_RECLAMATION.action}
        lignes={2}
      />
      <ZoneTexte
        id={`${id}-amelioration`}
        libelle="Piste d’amélioration"
        aide="Reprise dans l’export, pour l’analyse de l’amélioration continue (indicateur Qualiopi 32)."
        valeur={saisie.amelioration}
        onChange={changer('amelioration')}
        max={LIMITES_RECLAMATION.amelioration}
        lignes={2}
      />
      {cloturer.error && <MessageErreur message={messageErreur(cloturer.error, 'La réclamation n’a pas pu être clôturée.')} />}
      <Actions enCours={cloturer.isPending} libelle="Clôturer" onAnnuler={onFait} valide={valide} />
    </form>
  );
}

function FormulaireReouverture({ reclamation, onFait }: { reclamation: VueReclamation; onFait: () => void }): JSX.Element {
  const id = useId();
  const toast = useToast();
  const rouvrir = useRouvrirReclamation();
  const [motif, setMotif] = useState('');
  const valide = motif.trim().length >= LIMITES_RECLAMATION.motif.min;

  return (
    <form
      className="space-y-3 rounded-xl bg-gray-50 p-3 sm:p-4"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (!valide) return;
        rouvrir.mutate(
          { id: reclamation.id, corps: { motif: motif.trim() } },
          {
            onSuccess: () => {
              toast('Réclamation rouverte');
              onFait();
            },
          },
        );
      }}
    >
      <ZoneTexte
        id={`${id}-motif`}
        libelle="Motif de la réouverture"
        aide="La réponse et la conclusion d’avant restent dans l’historique ; le responsable est prévenu."
        valeur={motif}
        onChange={setMotif}
        max={LIMITES_RECLAMATION.motif.max}
        obligatoire
      />
      {rouvrir.error && <MessageErreur message={messageErreur(rouvrir.error, 'La réclamation n’a pas pu être rouverte.')} />}
      <Actions enCours={rouvrir.isPending} libelle="Rouvrir" onAnnuler={onFait} valide={valide} />
    </form>
  );
}
